<template>
  <div>
    <ag-grid-vue
      style="height: 650px"
      class="ag-theme-material"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      :rowData="rowData"
      :resizable="true"
      :filter="true"
      :floatingFilter="true"
      :localeTextFunc="localeTextFunc"
      :suppressCellSelection="true"
      @gridReady="onGridReady"
      @gridSizeChanged="adjustGrid"
      @firstDataRendered="adjustGrid"
      @rowDataChanged="adjustGrid"
    >
    </ag-grid-vue>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "ag-grid-enterprise";
import { agGridMixin } from "@/mixins/agGridMixin";
import UrlRenderer from "@/components/gridRenderers/UrlRenderer";
import NoFollowRenderer from "@/components/gridRenderers/NoFollowRenderer";
import ProgressRenderer from "@/components/gridRenderers/ProgressRenderer";

export default {
  mixins: [agGridMixin],
  components: {
    AgGridVue,
    UrlRenderer,
    NoFollowRenderer,
    ProgressRenderer,
  },
  props: {
    data: Object,
  },
  data() {
    return {
      columnDefs: null,
      defaultColDef: { sortable: true, resizable: true },
      rowData: null,
      gridApi: null,
      columnApi: null,
    };
  },
  methods: {
    onGridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;
    },
    adjustGrid() {
      if (this.columnApi.columnController.scrollWidth > 0) {
        this.gridApi.sizeColumnsToFit();
      }
    },
    setColumnDefs() {
      this.columnDefs = [
        {
          headerName: this.$i18n.t("url_source"),
          field: "urlSource",
          filter: "agTextColumnFilter",
          width: 500,
          cellRendererFramework: "UrlRenderer",
        },
        {
          headerName: this.$i18n.t("url_data"),
          headerClass: "ag-centered-header",
          children: [
            {
              headerName: this.$i18n.t("citation_flow"),
              field: "citationFlowUrl",
              type: "numericColumn",
              filter: "agNumberColumnFilter",
              valueFormatter: numberFormatter,
              filterParams: {
                defaultOption: "greaterThan",
              },
              cellRendererFramework: "ProgressRenderer",
            },
            {
              headerName: this.$i18n.t("trust_flow"),
              field: "trustFlowUrl",
              type: "numericColumn",
              filter: "agNumberColumnFilter",
              valueFormatter: numberFormatter,
              filterParams: {
                defaultOption: "greaterThan",
              },
              cellRendererFramework: "ProgressRenderer",
            },
          ],
        },
        {
          headerName: this.$i18n.t("domain_data"),
          headerClass: "ag-centered-header",
          children: [
            {
              headerName: this.$i18n.t("citation_flow"),
              field: "citationFlowDomain",
              type: "numericColumn",
              filter: "agNumberColumnFilter",
              valueFormatter: numberFormatter,
              filterParams: {
                defaultOption: "greaterThan",
              },
              cellRendererFramework: "ProgressRenderer",
            },
            {
              headerName: this.$i18n.t("trust_flow"),
              field: "trustFlowDomain",
              type: "numericColumn",
              filter: "agNumberColumnFilter",
              valueFormatter: numberFormatter,
              filterParams: {
                defaultOption: "greaterThan",
              },
              cellRendererFramework: "ProgressRenderer",
            },
          ],
        },
        {
          headerName: this.$i18n.t("target_url"),
          field: "targetUrl",
          filter: "agTextColumnFilter",
          cellRendererFramework: "UrlRenderer",
          width: 400,
        },
        {
          headerName: this.$i18n.t("no_follow"),
          field: "noFollow",
          type: "numericColumn",
          width: 220,
          cellRendererFramework: "NoFollowRenderer",
        },
      ];
    },
    mapData() {
      if (!this.data || !this.data.data) {
        this.rowData = [];
      } else {
        if (this.data.data.length === 1) {
          this.rowData = this.data.data[0].attributes.urlBacklinkData.map(
            (backlink) => {
              return {
                urlSource: backlink.sourceUrl.url,
                domainAuthority: backlink.sourceUrl.metrics.domainAuthority,
                citationFlowUrl: backlink.sourceUrl.metrics.citationFlow,
                trustFlowUrl: backlink.sourceUrl.metrics.trustFlow,
                citationFlowDomain: backlink.sourceDomain.metrics.citationFlow,
                trustFlowDomain: backlink.sourceDomain.metrics.trustFlow,
                targetUrl: backlink.targetUrl.url,
                noFollow: backlink.noFollow ? true : false,
              };
            }
          );
        } else {
          this.rowData = [];
        }
      }
    },
  },
  beforeMount() {
    this.setColumnDefs();
  },
  mounted() {
    if (this.data) {
      this.mapData();
    }
  },
  watch: {
    data() {
      this.mapData();
    },
  },
};

let formatNumber = (number) => {
  return Math.floor(number)
    .toString()
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ");
};
let numberFormatter = (params) => formatNumber(params.value);
</script>

<style scoped lang="scss">
::v-deep .ag-centered-header {
  .ag-header-group-cell-label {
    justify-content: center;
  }
}
</style>

<i18n src="@/javascripts/grid.json"></i18n>
<i18n>
{
  "en": {
    "url_source": "Source URL",
    "domain_authority": "DA",
    "citation_flow": "CF",
    "trust_flow": "TF",
    "no_follow": "No Follow ?",
    "target_url": "Target URL",
    "url_data": "Flow metrics of the URL",
    "domain_data": "Flow metrics of the domain"
  },
  "fr": {
    "url_source": "URL Source",
    "domain_authority": "DA",
    "citation_flow": "CF",
    "trust_flow": "TF",
    "no_follow": "No Follow ?",
    "target_url": "Url ciblée",
    "url_data": "Données flow metrics de l'url",
    "domain_data": "Données flow metrics du domaine"
  },
  "de": {
    "url_source": "URL-Quelle",
    "domain_authority": "UND",
    "citation_flow": "CF",
    "trust_flow": "TF",
    "no_follow": "Nein folgen?",
    "target_url": "Gezielte URL",
    "url_data": "URL-Flow-Metrikdaten",
    "domain_data": "Daten zu Domänenflussmetriken"
  }
}
</i18n>
